import { useId, useState } from 'react'
import { useT } from './i18n-context.js'

/**
 * Заголовок раздела со свёрнутой подсказкой.
 *
 * Подсказки на экране мастера объясняют устройство приложения:
 * что ссылка одна на кампанию, что предел — не список мест, что
 * отдых объявляется всем сразу. Каждая написана для того, кто видит
 * экран впервые, и каждая по отдельности полезна. Вместе они
 * занимали больше места, чем партия и очередь боя, — то есть чем то,
 * ради чего мастер экран и открыл.
 *
 * Поэтому текст никуда не делся, но встал за знак вопроса рядом
 * с заголовком: одна строка вместо трёх абзацев, и разворачивается
 * она тем, кому нужна. Новичок нажмёт, знающий не увидит.
 *
 * Раскрытая подсказка — не всплывающее окно: она встаёт в поток
 * и раздвигает содержимое. Всплывающее пришлось бы позиционировать,
 * закрывать по нажатию мимо и уводить с экрана на телефоне, а даёт
 * оно ровно то же самое.
 */
export function SectionHead({
  title,
  hints,
  id,
}: {
  readonly title: string
  /** Тексты подсказок; пустой список — знака вопроса нет вовсе. */
  readonly hints?: readonly string[] | undefined
  /** Идентификатор заголовка: нужен, когда на него ссылаются. */
  readonly id?: string | undefined
}) {
  const t = useT()
  const [open, setOpen] = useState(false)
  const panel = useId()
  const texts = hints ?? []

  return (
    <div className="app-section-head">
      <div className="app-section-title">
        <h3 id={id}>{title}</h3>
        {texts.length > 0 && (
          <button
            type="button"
            className="app-hint-toggle"
            aria-expanded={open}
            aria-controls={panel}
            aria-label={t('app.hint', { section: title })}
            onClick={() => {
              setOpen((current) => !current)
            }}
          >
            ?
          </button>
        )}
      </div>
      {open && (
        <div className="app-hint-text" id={panel}>
          {texts.map((text) => (
            <p key={text} className="app-field-hint">
              {text}
            </p>
          ))}
        </div>
      )}
    </div>
  )
}
